// Amazon multi-item cart handoff for completed builds.
//
// A finished build is a parts list, and Amazon's add-to-cart form accepts
// repeated ASIN.N / Quantity.N pairs alongside the Associates tag. That lets one
// click hand the entire list over with every line item attributed to us, which
// is worth far more than seven separate product-page visits.
//
// Associates policy notes (checked 2026-08-15):
//   * The Operating Agreement requires the tracking id to travel as a parameter
//     in the URL, and since the 2026-04-14 update only ONE tag may be used to
//     attribute a commission. AssociateTag is therefore the only tag we attach.
//   * A customer may only reach Amazon by clicking a Special Link, so the URLs
//     built here are exclusively used as the href of an anchor the user clicks.
//     Nothing in this module navigates, prefetches, or auto-submits.
//   * The bulk form is no longer covered by live Amazon documentation: the
//     PA-API 5.0 "Add to Cart form" page now redirects to a deprecation notice.
//     The format still works, but because it is undocumented we keep the
//     per-part fallback one env var away (AMAZON_CART_MODE=sequential) and always
//     render the individual product links next to the bulk CTA.

import { AMAZON_CART_LINK, AMAZON_CART_MODE, AMAZON_TAG, sanitizeAmazonAsin } from '../config'

// The cart URL travels through a browser address bar and an Amazon redirect, so
// keep it well clear of any practical length limit. A seven-slot build with
// multi-item RAM/storage/GPU never approaches this.
export const MAX_CART_ITEMS = 25

// Amazon rejects oversized line quantities outright; a build slot never needs
// more than a handful of identical parts.
export const MAX_CART_QUANTITY = 30

const clampQuantity = (value) => {
    var quantity = parseInt(value, 10)
    if(isNaN(quantity) || quantity < 1){
        return 1
    }
    return Math.min(quantity, MAX_CART_QUANTITY)
}

export const rowAsin = (row) => {
    if(!row){ return '' }
    return sanitizeAmazonAsin(row.asin || (row.item && row.item.asin))
}

// Rows that carry a usable ASIN, merged by ASIN so two slots holding the same
// listing become one cart line with a summed quantity.
export function cartEntries(rows){
    var entries = []
    var byAsin = {}

    ;(rows || []).forEach((row) => {
        if(!row || !row.item){ return }
        var asin = rowAsin(row)
        if(!asin){ return }

        var quantity = clampQuantity(row.qty)
        if(byAsin[asin]){
            byAsin[asin].quantity = clampQuantity(byAsin[asin].quantity + quantity)
            byAsin[asin].rows.push(row)
            return
        }

        var entry = { asin: asin, quantity: quantity, rows: [row] }
        byAsin[asin] = entry
        entries.push(entry)
    })

    return entries
}

export function buildCartUrl(entries, tag){
    var list = (entries || []).filter((entry) => entry && entry.asin).slice(0, MAX_CART_ITEMS)
    if(!list.length){
        return null
    }

    var params = ['AssociateTag=' + encodeURIComponent(tag || AMAZON_TAG)]
    list.forEach((entry, index) => {
        var position = index + 1
        params.push('ASIN.' + position + '=' + encodeURIComponent(entry.asin))
        params.push('Quantity.' + position + '=' + clampQuantity(entry.quantity))
    })

    return AMAZON_CART_LINK + '?' + params.join('&')
}

// Single-part cart link, used by the sequential fallback. Same form, one line.
export function partCartUrl(row, tag){
    var asin = rowAsin(row)
    if(!asin){ return null }
    return buildCartUrl([{ asin: asin, quantity: clampQuantity(row && row.qty) }], tag)
}

// Everything a buy surface needs to render the CTA: which rows made it into the
// cart, which could not, and the URL(s) to use for the active mode.
export function cartPlan(rows, options){
    var settings = options || {}
    var mode = settings.mode || AMAZON_CART_MODE
    var tag = settings.tag || AMAZON_TAG

    var selected = (rows || []).filter((row) => row && row.item)
    var included = selected.filter((row) => !!rowAsin(row))
    var excluded = selected.filter((row) => !rowAsin(row))

    var entries = cartEntries(included)
    var capped = entries.slice(0, MAX_CART_ITEMS)

    return {
        mode: mode,
        entries: capped,
        // Bulk mode gets one URL for the whole list; sequential mode deliberately
        // has none so a surface cannot accidentally fire seven popup-blocked tabs.
        url: mode === 'bulk' ? buildCartUrl(capped, tag) : null,
        steps: mode === 'sequential'
            ? included.map((row) => ({ row: row, url: partCartUrl(row, tag) })).filter((step) => step.url)
            : [],
        includedRows: included,
        excludedRows: excluded,
        selectedCount: selected.length,
        includedCount: included.length,
        excludedCount: excluded.length,
        lineCount: capped.length,
        unitCount: capped.reduce((total, entry) => total + entry.quantity, 0),
        truncated: entries.length > capped.length,
    }
}

// "2 of 7 parts unavailable on Amazon, listed below" -- stated plainly rather
// than silently dropping the part from the cart. Pass listed: false where no
// parts table follows, so the sentence does not point at something absent.
export function excludedPartsNotice(plan, options){
    if(!plan || !plan.excludedCount){
        return ''
    }
    var listed = !options || options.listed !== false
    return plan.excludedCount
        + ' of ' + plan.selectedCount
        + (plan.selectedCount === 1 ? ' part is' : ' parts are')
        + ' unavailable on Amazon'
        + (listed ? ', listed below' : ' and could not be added to the cart')
}
